<template>
	<div>
		<div class="table_head table_head_filter" style="height: 65px;">
			<div class="flex flex_center">
				<md-switch v-model="showAllColumns">
					{{ $t("common.table.switch.showAllColumns") }}
				</md-switch>
				<AlfaButtonGroup :status="(alfaStatus || {}).status" @show="showAlfaSettingsModal" @start="confirmStartAlfa" @stop="stopAlfa"/>
				<div style="margin-left: 4rem" v-if="lastStatisticsUpdateTime">
					<i class="fa fa-info-circle secondary_color" aria-hidden="true" style="margin-right: 0.5rem"></i>
					<span>
						{{$t("investor.exchange.lastStatisticsUpdate", { updateTime: lastStatisticsUpdateTime }) }}
					</span>
				</div>
				<div style="margin-left: 4rem" v-if="lastTCILimitsUpdateTime">
					<i class="fa fa-info-circle secondary_color" aria-hidden="true" style="margin-right: 0.5rem"></i>
					<span>
						{{$t("investor.exchange.lastTCILimitsUpdate", { updateTime: lastTCILimitsUpdateTime }) }}
					</span>
				</div>
			</div>
			<div class="flex tcilogoandbutton">
				<div class="text_right">
					<img src="../../../../../assets/img/one_underwriting_logo.png" style="width: 70px;"/>
				</div>

				<ActivateTciButton v-if="hideTci"/>

				<div class="text_right" style="width: 350px;">
					<span>{{ $t("investor.exchange.table.selectedBids") }}</span>
					<br/>
					<span class="font_bold accent_color">{{ totalSelectedBidAmount }}</span>
					<br/>
					<span class="accent_color">{{
							$t("investor.exchange.table.selectedPremiums", { premiums: totalSelectedBidPremiums })
						}}</span>
				</div>
			</div>
		</div>
		<div class="flex filter_table">
			<div class="flex flex_column w_100">
				<ag-item :rowData="auctionList"
						 :columnDefs="columnDefs"
						 @row-select="onRowSelect"
						 v-on:sortChanged="sortChanged"
						 :with-button-group="false"
						 :with-tabs="true"
						 :with-header="true"
						 :loading-warning-text="accountsWarningText"
						 :key="accountsWarningText"
						 :excelStyles="excelStyles"
						 ref="exchangeReceivablesTable"
				></ag-item>
			</div>
			<ExchangeReceivablesFilter :rating-config="ratingConfig" @apply-filter="applyFilter"/>
		</div>
		<AlfaSettingsModal ref="alfaSettingsModal" :account="activeAccount" :tci-confirmation-status="tciConfirmationStatus" @close="loadAlfaStatus"/>
		<ExchangeReceivableNoteModel ref="exchangeReceivableNoteModal"/>
		<BidsResponseDialog ref="bidsResponseDialog"/>
	</div>
</template>

<script>
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import BidsResponseDialog from "src/components/templates/investor/exchange/receivables/BidsResponseDialog";
import ExchangeReceivableNoteModel
	from "@/components/templates/investor/exchange/receivables/ExchangeReceivableNoteModel";
import {mapActions, mapGetters} from "vuex";
import {exchangeReceivablesApi} from "@/common/api/exchangeReceivablesApi";
import {formatAsMoneyAmount} from "@/common/format";
import {
	asDecimal,
	asHidden,
	asIndustryText,
	asIsOrNotAvailableIfOtherCellIsZero,
	asLocalizedDate,
	asMoneyAmount,
	asPercentage
} from "@/components/templates/agGrid/formatter/valueFormatters";
import {enumDef} from "@/common/enumDefs";
import ExchangeReceivablesFilter from "@/components/templates/investor/exchange/receivables/ExchangeReceivablesFilter";
import {asNullIfOtherFieldIsTrue} from "@/components/templates/agGrid/getter/valueGetters";
import AlfaSettingsModal from "@/components/templates/investor/exchange/alfa/AlfaSettingsModal";
import {alfaApi} from "@/common/api/alfaApi";
import AlfaButtonGroup from "@/components/templates/investor/exchange/alfa/AlfaButtonGroup.vue";
import {hasAccess} from "@/common/permission/permission";
import ActivateTciButton from "@/components/templates/investor/exchange/receivables/ActivateTciButton.vue";

export default {
	components: {
		ActivateTciButton,
		AlfaButtonGroup,
		AlfaSettingsModal,
		ExchangeReceivablesFilter,
		ExchangeReceivableNoteModel,
		AgItem,
		BidsResponseDialog,
	},
	props: {
		activeAccount: { type: Object, default: null },
		accountsWarningText: { type: String, default: "" },
		ratingConfig: { type: Object, default: null },
		tciConfirmationStatus: { type: Number, default: enumDef.creditInsurancePolicyHolderStatus.NONE },
		ready: { type: Boolean, default: false }
	},
	data() {
		return {
			auctionList: null,
			filters: [],
			sorting: [],
			columnDefs: [],
			showAllColumns: false,
			lastStatisticsUpdateTime: null,
			lastTCILimitsUpdateTime: null,
			excelStyles: [{
				id: "round_for_excel_export",
				numberFormat: {
					format: "#.00_#",
				},
			}],
			alfaStatus: null,
		};
	},
	computed: {
		...mapGetters(["exchangeCheckedRows"]),
		totalSelectedBidAmount() {
			let amount = this.exchangeCheckedRows
				.map((data) => data.finalBuyPrice)
				.reduce((bid1, bid2) => bid1 + bid2, 0);

			return formatAsMoneyAmount(amount, {
				currency: this.activeAccount ? this.activeAccount.tbCurrType : null
			});
		},
		totalSelectedBidPremiums() {
			return formatAsMoneyAmount(this.getOverallPremiums());
		},
		activeAccountId() {
			return this.activeAccount.id;
		},
		hideTrafficLight() {
			return this.$sessionStorage.get("localeUser").hideTrafficLight;
		},
		canBuyTci() {
			return enumDef.creditInsurancePolicyHolderStatus.canBuyTci(this.tciConfirmationStatus);
		},
		canOnlyViewTci() {
			return enumDef.creditInsurancePolicyHolderStatus.hasRequestedAccess(this.tciConfirmationStatus);
		},
		hideTci() {
			return !enumDef.creditInsurancePolicyHolderStatus.hasTciAccess(this.tciConfirmationStatus);
		}
	},
	beforeMount() {
		if (!this.ready) {
			return;
		}
		
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		getOverallPremiums() {
			return this.exchangeCheckedRows
				.filter(data => data.useInsurance)
				.map((data) => data.insuranceGrossPremiumAmount || 0)
				.reduce((value1, value2) => value1 + value2, 0);
		},
		...mapActions(["setActionCheckedRowsInExchange", "clearActionCheckedRowsInExchange", "setActionActiveAccount"]),
		setColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t("common.debtor"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.debtor.tooltip"),
					},
					children: [
						{
							headerName: this.$t("common.name"),
							field: "debtorName",
							minWidth: 175,
							nameType: "debtorName",
							idType: "debtorId",
							cellRendererFramework: "CompanyTem",
							sortable: true
						},
						{
							headerName: this.$t("common.cofaceDra"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.debtor.dra.tooltip"),
							},
							field: "debtorCofaceDra",
							minWidth: 70,
							cellRendererFramework: "CofaceDraRenderer",
							cellRendererParams: {
								draStatusField: "debtorCofaceDraStatus"
							},
							sortable: true
						},
						{
							headerName: this.$t("common.tci.riskScore"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.debtor.riskScore.tooltip"),
							},
							field: "debtorInsuranceRiskScore",
							valueFormatter: asDecimal({ decimals: 0, showZeroAsNotAvailable: true, showEmptyAsNotAvailable: true }),
							minWidth: 150,
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating1,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating1
								: this.$t("investor.exchange.table.header.customRating1"),
							field: "debtorRating1",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating2,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating2
								: this.$t("investor.exchange.table.header.customRating2"),
							field: "debtorRating2",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating3,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating3
								: this.$t("investor.exchange.table.header.customRating3"),
							field: "debtorRating3",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true,
						},
						{
							headerName: this.$t("investor.exchange.table.header.industry"),
							field: "debtorIndustryId",
							minWidth: 250,
							valueFormatter: asIndustryText(),
							cellRendererFramework: "GeneralShortenedDisplay",
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.country"),
							field: "debtorCountry",
							minWidth: 90,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.city"),
							field: "debtorCity",
							minWidth: 75,
							columnGroupShow: "open",
						},
						{
							headerName: this.$t("common.postcode"),
							field: "debtorPostcode",
							minWidth: 100,
							columnGroupShow: "open",
						},
						{
							headerName: this.$t("common.street"),
							field: "debtorStreet",
							minWidth: 75,
							columnGroupShow: "open",
						},
						{
							headerName: this.$t("common.vatId"),
							field: "debtorVatNumber",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.all"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.all.tooltip"),
							},
							headerClass: "align-right",
							field: "numDebtorAll",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaid.tooltip"),
							},
							headerClass: "align-right",
							field: "numDebtorUnpaid",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue.tooltip"),
							},
							headerClass: "align-right",
							field: "numDebtorUnpaidAndOverdue",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate.tooltip"),
							},
							headerClass: "align-right",
							field: "debtorAverageDaysAfterDueDate",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asDecimal(),
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid.tooltip"),
							},
							headerClass: "align-right",
							field: "numDebtorFullyPaid",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysBetweenLastPaymentDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysBetweenLastPaymentDueDate.tooltip"),
							},
							headerClass: "align-right",
							field: "debtorAverageDaysBetweenLastPaymentDueDate",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount.tooltip"),
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmount",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid.tooltip"),
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountUnpaid",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue.tooltip"),
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountUnpaidAndOverdue",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid.tooltip"),
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountFullyPaid",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay.tooltip"),
							},
							headerClass: "align-right",
							field: "sumDebtorSoldAmountFullyPaidAndInDelay",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						}
					]
				},
				{
					headerName: this.$t("common.seller"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.seller.tooltip"),
					},
					children: [
						{
							headerName: this.$t("common.name"),
							minWidth: 175,
							field: "sellerName",
							nameType: "sellerName",
							idType: "sellerId",
							cellRendererFramework: "CompanyTem",
							sortable: true
						},
						{
							headerName: this.$t("common.cofaceDra"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.seller.dra.tooltip"),
							},
							field: "sellerCofaceDra",
							minWidth: 70,
							cellRendererFramework: "CofaceDraRenderer",
							cellRendererParams: {
								draStatusField: "sellerCofaceDraStatus"
							},
							sortable: true
						},
						{
							headerName: this.$t("common.tci.riskScore"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.debtor.riskScore.tooltip"),
							},
							field: "sellerInsuranceRiskScore",
							valueFormatter: asDecimal({ decimals: 0, showZeroAsNotAvailable: true, showEmptyAsNotAvailable: true }),
							minWidth: 150,
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.seller.partner"),
							field: "partnerName",
							minWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating1,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating1
								: this.$t("investor.exchange.table.header.customRating1"),
							field: "sellerRating1",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating2,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating2
								: this.$t("investor.exchange.table.header.customRating2"),
							field: "sellerRating2",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							hide: !this.ratingConfig || !this.ratingConfig.showRating3,
							headerName: this.ratingConfig
								? this.ratingConfig.nameRating3
								: this.$t("investor.exchange.table.header.customRating3"),
							field: "sellerRating3",
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 140,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.industry"),
							field: "sellerIndustryId",
							minWidth: 250,
							valueFormatter: asIndustryText(),
							cellRendererFramework: "GeneralShortenedDisplay",
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.country"),
							field: "sellerCountry",
							minWidth: 90,
							maxWidth: 125,
							columnGroupShow: "open",
							sortable: true
						},

						{
							headerName: this.$t("common.city"),
							field: "sellerCity",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.postcode"),
							field: "sellerPostcode",
							minWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.street"),
							field: "sellerStreet",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("common.vatId"),
							field: "sellerVatNumber",
							minWidth: 75,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.supplier"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.supplier.tooltip"),
							},
							headerClass: "border",
							minWidth: 200,
							field: "supplierName",
							nameType: "supplierName",
							idType: "supplierId",
							cellRendererFramework: "CompanyTem",
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.all"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.all.tooltip"),
							},
							headerClass: "align-right",
							field: "numSupplierAll",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaid.tooltip"),
							},
							headerClass: "align-right",
							field: "numSupplierUnpaid",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.unpaidAndOverdue.tooltip"),
							},
							headerClass: "align-right",
							field: "numSupplierUnpaidAndOverdue",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysAfterDueDate.tooltip"),
							},
							headerClass: "align-right",
							field: "supplierAverageDaysAfterDueDate",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asDecimal(),
							minWidth: 100,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.fullyPaid.tooltip"),
							},
							headerClass: "align-right",
							field: "numSupplierFullyPaid",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysBetweenLastPaymentDueDate"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.averageDaysBetweenLastPaymentDueDate.tooltip"),
							},
							headerClass: "align-right",
							field: "supplierAverageDaysBetweenLastPaymentDueDate",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asDecimal(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmount.tooltip"),
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmount",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaid.tooltip"),
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountUnpaid",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountUnpaidAndOverdue.tooltip"),
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountUnpaidAndOverdue",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaid.tooltip"),
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountFullyPaid",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.soldAmountFullyPaidAndInDelay.tooltip"),
							},
							headerClass: "align-right",
							field: "sumSupplierSoldAmountFullyPaidAndInDelay",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asMoneyAmount(),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						}
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.riskAnalysis"),
					headerGroupComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.riskAnalysis.tooltip"),
					},
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.dsrConfirmed"),
							field: "dsrConfirmed",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 130,
							maxWidth: 130,
							cellRendererFramework: "BooleanAsIcon",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.directPay"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.directPay.tooltip"),
							},
							headerClass: "align-right",
							field: "assessmentDirectPay",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							maxWidth: 100,
							cellRendererFramework: "BooleanAsIcon",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.quantity"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.quantity.tooltip"),
							},
							headerClass: "align-right",
							field: "assessmentQuantity",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 80,
							maxWidth: 100,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.latePaymentDays.tooltip"),
							},
							headerClass: "align-right",
							field: "assessmentLatePaymentDays",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asIsOrNotAvailableIfOtherCellIsZero("assessmentQuantity"),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.riskAnalysis.currentOutstanding"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.riskAnalysis.currentOutstanding.tooltip"),
							},
							headerClass: "align-right",
							field: "assessmentOverdueOutstanding",
							cellStyle: { "justify-content": "flex-end" },
							valueFormatter: asIsOrNotAvailableIfOtherCellIsZero("assessmentQuantity"),
							minWidth: 120,
							maxWidth: 120,
							columnGroupShow: "open",
							sortable: true
						}
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.receivableDetails"),
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.receivableType"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.receivableType.tooltip"),
							},
							field: "receivableType",
							minWidth: 65,
							maxWidth: 80,
							cellRendererFramework: "EnumAsIcon",
							enumDef: enumDef.receivableType,
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.receivableAmount"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.receivableAmount.tooltip"),
							},
							headerClass: "align-right",
							field: "receivableAmount",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							valueFormatter: asMoneyAmount(),
							sortable: true
						},
						{
							headerName: this.$t("common.vatRate"),
							headerClass: "align-right",
							field: "receivableVatRate",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							valueFormatter: asPercentage( { default: "0%", showEmptyAsNotAvailable: true, decimals: 0 }),
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.receivableNetAmount"),
							headerClass: "align-right",
							field: "receivableNetAmount",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 125,
							valueFormatter: asMoneyAmount(),
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.dueDate"),
							field: "dueDate",
							minWidth: 150,
							valueFormatter: asLocalizedDate({ showDaysLeft: true }),
							sortable: true
						},
						{
							headerName: this.$t("common.applicableLaw.short"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.applicableLaw.tooltip"),
							},
							field: "applicableLaw",
							minWidth: 110,
							maxWidth: 130,
							columnGroupShow: "open",
							sortable: true
						},
						{
							headerName: this.$t("investor.exchange.table.header.invoiceNumberAndDocuments"),
							field: "invoiceNumber",
							minWidth: 175,
							columnGroupShow: "open",
							sortable: true,
							cellRendererFramework: "ItemDetail",
						},
						{
							headerName: this.$t("investor.exchange.table.header.assignmentType"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.assignmentType.tooltip"),
							},
							field: "postTrade",
							minWidth: 180,
							cellRendererFramework: "EnumAsText",
							enumDef: enumDef.postTradeType,
							columnGroupShow: "open"
						}
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.retention"),
					headerClass: "bg_special",
					headerComponentParams: {
						tooltip: this.$t("investor.exchange.table.header.retention.tooltip"),
					},
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.retention"),
							headerComponentFramework: "RetentionSwitchHeader",
							headerComponentParams: {
								useRetentionField: "useRetention",
							},
							field: "allowedRetentionRate",
							valueGetter: asNullIfOtherFieldIsTrue("useRetention"),
							minWidth: 100,
							maxWidth: 100,
							cellRendererFramework: "RetentionSwitch",
							cellRendererParams: {
								retentionMandatoryField: "isRetentionMandatory",
								useRetentionField: "useRetention",
								retentionRateField: "allowedRetentionRate",
								currencyField: "currency",
								onChange: this.updatePrices
							},
							pinned: "right"
						}
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.salesOffer"),
					headerClass: "bg_special",
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.currentRoi"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.instantBuy.currentRoi.tooltip"),
							},
							headerClass: "align-right",
							field: "instantBuyRoi",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 130,
							maxWidth: 130,
							valueFormatter: asDecimal({ showPercentSign: true }),
							sortable: true,
							pinned: "right"
						},
						{
							hide: this.hideTrafficLight,
							headerName: this.$t("common.grade"),
							field: "trafficLightState",
							minWidth: 75,
							maxWidth: 75,
							cellRendererFramework: "ColorStatus",
							cellRendererParams: {
								color: (data) => enumDef.trafficLight.properties[data.trafficLightState]
							},
							sortable: true,
							pinned: "right"
						}
					]
				},
				{
					headerName: this.$t("investor.exchange.table.header.tci"),
					headerClass: "bg_special",
					children: [
						{
							headerName: this.$t("investor.exchange.table.header.tci"),
							headerComponentFramework: "TCISwitchHeader",
							headerComponentParams: {
								canBuyTci: this.canBuyTci
							},
							field: "useInsurance",
							cellStyle: this.hideTci ? { backgroundColor: '#E6E7E8' } : null,
							minWidth: 60,
							maxWidth: 60,
							cellRendererFramework: "TCISwitch",
							cellRendererParams: {
								isInsuranceDisabledField: "isInsuranceDisabled",
                				canBuyTci: this.canBuyTci,
								canOnlyViewTci: this.canOnlyViewTci,
								onChange: this.updatePrices
							},
							pinned: "right"
						},
						{
							headerName: this.$t("common.tci.premium"),
							headerClass: "align-right",
							field: "insuranceGrossPremiumAmount",
							cellStyle: this.hideTci
								? {
									"justify-content": "flex-end" ,
									"backgroundColor": '#E6E7E8'
								}
								: {
									"justify-content": "flex-end"
								},
							minWidth: 90,
							maxWidth: 90,
							cellRendererFramework: "TCIPremium",
							cellRendererParams: {
								grossPremiumAmountField: "insuranceGrossPremiumAmount",
								netPremiumAmountField: "insuranceNetPremiumAmount",
								currencyField: "currency",
								useInsuranceField: "useInsurance",
								canBuyTci: this.canBuyTci,
								canOnlyViewTci: this.canOnlyViewTci
							},
							sortable: true,
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.currentRoiWithTci"),
							headerComponentParams: {
								tooltip: this.$t("investor.exchange.table.header.instantBuy.currentRoiWithTci.tooltip"),
							},
							headerClass: "align-right wrap-text",
							field: "instantBuyRoiWithTci",
							cellStyle: this.hideTci
								? {
									"justify-content": "flex-end" ,
									"backgroundColor": '#E6E7E8'
								}
								: {
									"justify-content": "flex-end"
								},
							minWidth: 130,
							maxWidth: 130,
							valueFormatter: this.hideTci ? asHidden() : asDecimal({ showPercentSign: true, showEmptyAsNotAvailable: true }),
							sortable: true,
							pinned: "right"
						},
					]
				},
				{
					headerClass: "bg_special",
					children: [
						{
							headerName: this.$t("common.price"),
							headerClass: "align-right",
							field: "totalPrice",
							cellStyle: { "justify-content": "flex-end" },
							minWidth: 100,
							maxWidth: 100,
							valueFormatter: asMoneyAmount(),
							pinned: "right"
						},
						{
							headerComponentFramework: "BiddingBuyButtonHeader",
							headerComponentParams: {
								bidOnAllSelected: this.placeAllSelectedBids,
							},
							minWidth: 100,
							maxWidth: 100,
							cellRendererFramework: "BiddingBuyButton",
							cellRendererParams: {
								bidOnSingle: this.placeBid,
							},
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.timeLeft"),
							field: "endTime",
							minWidth: 100,
							maxWidth: 100,
							cellRendererFramework: "TimeLeftCountdown",
							sortable: true,
							sort: "asc",
							pinned: "right"
						},
						{
							headerName: this.$t("investor.exchange.table.header.note"),
							field: "comment",
							minWidth: 80,
							maxWidth: 80,
							cellRendererFramework: "ReceivableInvestorNoteIcon",
							cellRendererParams: {
								showNoteModal: this.showExchangeReceivableNoteModel,
							},
							sortable: true,
							pinned: "right"
						},
						{
							minWidth: 45,
							maxWidth: 45,
							headerCheckboxSelection: true,
							checkboxSelection: true,
							headerCheckboxSelectionFilteredOnly: true,
							pinned: "right",
						}
					]
				}
			];
		},
		sortChanged(sortData) {
			this.sorting = sortData;
			this.getList();
		},
		getList() {
			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: 25000,
				offset: 0,
				sorting: this.sorting
			};

			return new Promise((resolve, reject) => {
				exchangeReceivablesApi.inspection.listAuctions(this.activeAccountId, postData)
					.then((res) => {
							this.auctionList = res.data.map(item => {
								return {
									...item,
									// Make sure data for input field cell renderers and defaults are initialized before the table renders its data
									useRetention: item.isRetentionMandatory,
									useInsurance: this.canBuyTci && !item.isInsuranceDisabled,
									comment: item.comment || null,
									finalBuyPrice: null,
									totalPrice: null,
								};
						}).map(item => {
							return {
								...item,
								// Set up the initial prices after we have all the defaults configured
								finalBuyPrice: this.calculateFinalBuyPrice(item),
								totalPrice: this.calculateTotalPrice(item),
							}
						});
						this.clearActionCheckedRowsInExchange();
						this.updateLastStatisticsUpdateTime();
						this.updateLastTCILimitsUpdateTime();
						resolve();
					})
					.catch((e) => {
						this.toast(e);
						reject();
					});
			});
		},
		showExchangeReceivableNoteModel(data) {
			this.$refs.exchangeReceivableNoteModal.showNoteModal(data);
		},
		showAlfaSettingsModal() {
			this.$refs.alfaSettingsModal.showForSettings(this.alfaStatus.id);
		},
		calculateFinalBuyPrice(data) {
			return Number((data.instantBuyPrice * (1 - (data.useRetention ? data.allowedRetentionRate : 0))).toFixed(2))
		},
		calculateTotalPrice(data) {
			return Number((this.calculateFinalBuyPrice(data) + (data.useInsurance ? data.insuranceGrossPremiumAmount : 0)).toFixed(2));
		},
		updatePrices(params) {
			params.data.finalBuyPrice = this.calculateFinalBuyPrice(params.data);
			params.data.totalPrice = this.calculateTotalPrice(params.data);

			// Don't know why, but for some reason vue does not pick up on the price changes otherwise
			params.api.refreshCells({
				rowNodes: [params.node],
				// Do not specify columns to avoid problems when column defs are set multiple times and column ids have changed
			});
		},
		placeBid(data) {
			this.confirmPlaceBids([this.buildBid(data)]);
		},
		placeAllSelectedBids() {
			this.confirmPlaceBids(this.exchangeCheckedRows.map(this.buildBid), false);
		},
		buildBid(data) {
			return {
				data: data,
				amount: data.finalBuyPrice,
				type: enumDef.bidType.InstantBuy,
				retentionUsed: data.useRetention,
				withInsurance: data.useInsurance
			};
		},
		confirmPlaceBids(bids) {
			let companyCountry = this.currentUser.companyCountry;
			let bidInvolvingForeignCountries = bids.find(bid =>
				companyCountry !== bid.data.sellerCountry || companyCountry !== bid.data.supplierCountry || companyCountry !== bid.data.applicableLaw
			);
			let bidsCount = bids.filter(bid => bid.type === enumDef.bidType.Bid).length;
			let instantBuysCount = bids.filter(bid => bid.type === enumDef.bidType.InstantBuy).length;
			let insuranceBuysCount = bids.filter(bid => bid.withInsurance).length;

			let msg = "";
			if (bidsCount > 0) {
				msg += this.$t("investor.exchange.table.operation.bid.confirm.msg.bids", { count: bidsCount });
			}
			if (bidsCount > 0 && instantBuysCount > 0) {
				msg += "<br/>";
			}
			if (instantBuysCount > 0) {
				msg += this.$t("investor.exchange.table.operation.bid.confirm.msg.instantBuys", { count: instantBuysCount });
			}
			if (bidInvolvingForeignCountries) {
				msg += "<br/><br/>"
					+ "<div class='light_bg_color'>"
					+ this.$t("investor.exchange.table.operation.bid.foreignCountry.confirm.msg")
					+ "</div>";
			}
			if (insuranceBuysCount > 0) {
				msg += this.$t("investor.exchange.table.operation.bid.confirm.msg.insuranceBuys", { count: insuranceBuysCount });
				msg += "<br/><br/>" +
					"<div class='text_left light_bg_color margin_top w_100'>" +
					this.$t("investor.exchange.table.note") +
					"</div>";
			}

			let option = {
				title: this.$t("investor.exchange.table.operation.bid.confirm.title"),
				msg: msg,
				confirmFun: this.sendBids,
				params: bids
			};
			this.confirmMeg(option, true);
		},
		sendBids(bids) {
			let bidsPostData = bids.map(bid => {
				return {
					auctionId: bid.data.auctionId,
					accountId: this.activeAccountId,
					amount: bid.amount,
					type: bid.type,
					retentionUsed: bid.retentionUsed,
					withInsurance: bid.withInsurance
				};
			}, this);

			exchangeReceivablesApi.operation.placeBids(bidsPostData)
				.then((res) => {
					this.$refs.bidsResponseDialog.showWithResponse(res);
					this.getList();
				})
				.catch(this.toast);
		},
		onRowSelect(params) {
			this.setActionCheckedRowsInExchange(params.api.getSelectedRows());
		},
		applyFilter(filters) {
			this.filters = filters;
			this.getList();
		},
		updateLastStatisticsUpdateTime() {
			const STATISTICS_UPDATE_INTERVAL = 15;
			let lastIntervalMinutes = Math.floor(moment()
				.minutes() / STATISTICS_UPDATE_INTERVAL) * STATISTICS_UPDATE_INTERVAL;

			this.lastStatisticsUpdateTime = moment()
				.set("minute", lastIntervalMinutes)
				.format("HH:mm");
		},
		updateLastTCILimitsUpdateTime() {
			this.lastTCILimitsUpdateTime = moment()
				.format("HH:mm");
		},
		loadAlfaStatus() {
			if (!hasAccess(this.currentUser, enumDef.permissions.InvestorExchangeAlfa)) {
				return;
			}

			alfaApi.inspection.getCurrentStatus(this.activeAccountId)
				.then(res => {
					this.alfaStatus = res;
				})
				.catch(this.toast);
		},
		confirmStartAlfa() {
			let option = {
				title: this.$t("investor.exchange.alfa.start.confirm.title"),
				msg: this.buildHintBoxForConfirmation(this.$t("investor.exchange.alfa.start.confirm.msg", { stopButtonText: this.$t("investor.exchange.button.stopAlfa") }), false),
				confirmButtonText: this.$t("investor.exchange.button.startAlfa"),
				confirmButtonClass: "md-success",
				customClass: "msg_box_title_bold msg_box_width_l",
				confirmFun: this.startAlfa,
			};
			this.confirmMeg(option, true);
		},
		startAlfa() {
			alfaApi.operation.enable(this.alfaStatus.id)
				.then(() => this.alfaStatus.status = enumDef.alfaSettingsStatus.Active)
				.catch(this.toast);
		},
		stopAlfa() {
			alfaApi.operation.disable(this.alfaStatus.id)
				.then(() => this.alfaStatus.status = enumDef.alfaSettingsStatus.NotActive)
				.catch(this.toast);
		}
	},
	watch: {
		showAllColumns() {
			this.$refs.exchangeReceivablesTable.showAllColumns(this.showAllColumns);
		},
		activeAccount() {
			this.setActionActiveAccount(this.activeAccount);
			this.loadAlfaStatus();
			this.getList();
		},
		ready() {
			if (this.ready) {
				this.setColumnDefs();
			}
		}
	},
};
</script>

<style lang="scss" scoped>
@import "src/components/templates/investor/exchange/exchange";
  .tcilogoandbutton{
    width: 700px;
    justify-content: inherit;
    align-items: center;
  }
</style>
